import React from "react";
import BlogHero from "../blog/BlogHero";
import BlogQuote from "../blog/BlogQuote";
import BlogTip from "../blog/BlogTip";
import BlogImage from "../blog/BlogImage";
import BlogHeader from "../blog/BlogHeader";
import BlogFooter from "../blog/BlogFooter";

import {
	CalendarCheck,
	Clock,
	Users,
	Smartphone,
	Star,
	Gift,
	BarChart3,
} from "lucide-react";

import heroImg from "../../assets/blog/reservas-hero.jpg";
import ejemploImg from "../../assets/blog/reservas-ejemplo.jpg";

export default function PostReservas() {
	return (
		<div className="font-poppins text-gray-800">
			{/* Header fijo del blog */}
			<BlogHeader />

			{/* Hero principal */}
			<BlogHero
				title="Por qué las reservas online son clave para tu negocio"
				subtitle="Organiza tu tiempo, reduce llamadas y ofrece a tus clientes la comodidad de reservar en segundos."
				image={heroImg}
			/>

			<main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
				{/* Introducción */}
				<section>
					<p className="text-lg leading-relaxed mb-6">
						Gestionar las reservas por teléfono, por WhatsApp o en una libreta
						ya no es suficiente. Los clientes actuales esperan poder reservar en
						cualquier momento del día, desde su móvil y en menos de un minuto.
					</p>
					<p className="text-lg leading-relaxed">
						Ofrecer <strong>reservas online</strong> no es solo comodidad: es
						una forma de optimizar tu negocio, reducir errores y aumentar la
						satisfacción de tus clientes.
					</p>
				</section>

				{/* Beneficios principales */}
				<section>
					<h2 className="text-2xl font-bold mb-8">
						Principales beneficios de las reservas online
					</h2>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<CalendarCheck className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Disponibilidad 24/7:</strong> los clientes reservan a
								cualquier hora, incluso fuera del horario del negocio.
							</div>
						</li>
						<li className="flex items-start">
							<Clock className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Menos llamadas y errores:</strong> reduce la gestión
								manual y evita confusiones en la agenda.
							</div>
						</li>
						<li className="flex items-start">
							<Users className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Mejor experiencia del cliente:</strong> rapidez,
								comodidad y confirmaciones automáticas.
							</div>
						</li>
						<li className="flex items-start">
							<Smartphone className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Reservas desde cualquier dispositivo:</strong> móvil,
								tablet u ordenador, sin necesidad de apps externas.
							</div>
						</li>
						<li className="flex items-start">
							<Star className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Clientes más fieles:</strong> la comodidad aumenta la
								recurrencia y la satisfacción.
							</div>
						</li>
					</ul>
				</section>

				{/* Cita destacada */}
				<BlogQuote text="La mayoría de consumidores prefieren negocios que ofrecen reservas digitales frente a los que solo aceptan llamadas." />

				{/* Impacto en los datos del negocio */}
				{/* Impacto en los datos del negocio */}
				<section>
					<h2 className="text-2xl font-bold mb-6">
						Más reservas confirmadas, menos huecos vacíos
					</h2>
					<p className="text-lg leading-relaxed mb-6 text-gray-700">
						Al digitalizar tus reservas, no solo facilitas el proceso al
						cliente, sino que también mejoras la eficiencia interna. Se reducen
						las citas perdidas y aumentas la ocupación, ya que los clientes
						pueden reservar en cualquier momento, incluso fuera del horario de
						apertura.
					</p>

					<ul className="space-y-4 text-lg text-gray-700">
						<li className="flex items-start">
							<span className="mt-2 w-2 h-2 rounded-full bg-[#43bd98] mr-3 block flex-shrink-0" />
							<div>
								<strong>Confirmaciones automáticas:</strong> cada reserva queda
								registrada y validada al instante.
							</div>
						</li>
						<li className="flex items-start">
							<span className="mt-2 w-2 h-2 rounded-full bg-[#43bd98] mr-3 block flex-shrink-0" />
							<div>
								<strong>Recordatorios inteligentes:</strong> notificaciones push
								o emails que reducen ausencias.
							</div>
						</li>
						<li className="flex items-start">
							<span className="mt-2 w-2 h-2 rounded-full bg-[#43bd98] mr-3 block flex-shrink-0" />
							<div>
								<strong>Estadísticas en tiempo real:</strong> visualiza
								ocupación, horarios más demandados y evolución de reservas.
							</div>
						</li>
					</ul>
				</section>

				{/* Comparativa reservas manuales vs digitales */}
				<section className="bg-gray-50 rounded-2xl p-8 shadow-sm">
					<h2 className="text-2xl font-bold mb-6">
						Antes vs ahora: cómo ha cambiado reservar
					</h2>
					<div className="grid md:grid-cols-2 gap-8">
						<div>
							<h3 className="text-xl font-semibold mb-4 text-gray-900">
								Antes
							</h3>
							<ul className="space-y-3 text-gray-700">
								<li>Llamadas telefónicas con errores y esperas.</li>
								<li>Anotaciones manuales fáciles de perder.</li>
								<li>Clientes que olvidan la cita por falta de recordatorio.</li>
								<li>Solo se podían reservar en horario laboral.</li>
							</ul>
						</div>
						<div>
							<h3 className="text-xl font-semibold mb-4 text-gray-900">
								Ahora
							</h3>
							<ul className="space-y-3 text-gray-700">
								<li>Reservas online confirmadas al instante.</li>
								<li>Todo queda registrado en tu sistema, sin errores.</li>
								<li>Recordatorios automáticos que reducen ausencias.</li>
								<li>
									Reservas disponibles las 24 horas desde cualquier dispositivo.
								</li>
							</ul>
						</div>
					</div>
				</section>

				{/* Efecto en la fidelización */}
				<section>
					<h2 className="text-2xl font-bold mb-8">Efecto en la fidelización</h2>
					<p className="text-lg leading-relaxed mb-6">
						Una experiencia fluida de reserva no solo ahorra tiempo, también
						fortalece la relación con tus clientes. Cuando reservar es sencillo,
						los clientes repiten y se genera un vínculo más sólido con tu
						negocio.
					</p>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<Star className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Reservas sin fricción:</strong> si es rápido y fácil, el
								cliente elige repetir.
							</div>
						</li>
						<li className="flex items-start">
							<Users className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Experiencia personalizada:</strong> con el historial de
								reservas puedes adaptar la atención a las preferencias de cada
								cliente.
							</div>
						</li>
					</ul>
				</section>

				{/* Conexión con la estrategia de marketing */}
				<section>
					<h2 className="text-2xl font-bold mb-8">
						Conexión con la estrategia de marketing
					</h2>
					<p className="text-lg leading-relaxed mb-6">
						Las reservas online no son un módulo aislado. Integradas con tu
						estrategia digital, se convierten en una herramienta poderosa para
						atraer, retener y segmentar clientes.
					</p>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<Smartphone className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Notificaciones push:</strong> recuerda promociones y
								llena mesas en horas valle.
							</div>
						</li>
						<li className="flex items-start">
							<Gift className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Cupones automáticos:</strong> aplica descuentos o
								recompensas en la siguiente reserva sin esfuerzo.
							</div>
						</li>
						<li className="flex items-start">
							<BarChart3 className="w-7 h-7 md:w-6 md:h-6 text-[#43bd98] mr-3 flex-shrink-0 mt-0.5" />
							<div>
								<strong>Campañas segmentadas:</strong> dirige promociones según
								la frecuencia de asistencia y hábitos reales.
							</div>
						</li>
					</ul>
				</section>

				{/* Ejemplo visual */}
				<BlogImage
					src={ejemploImg}
					alt="Ejemplo de reservas online"
					caption="Con un sistema digital, tu agenda se organiza automáticamente y tus clientes reservan en segundos."
				/>

				{/* CTA */}
				<div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
					<h3 className="text-xl font-semibold mb-3 text-gray-900">
						¿Listo para dar el siguiente paso?
					</h3>
					<p className="text-gray-600 mb-5">
						Solicita una demo gratuita y descubre cómo las reservas online
						pueden transformar tu negocio.
					</p>
					<a
						href="https://applaby.com/app-demo/"
						target="_blank"
						rel="noreferrer"
						className="inline-block bg-[#43bd98] text-white px-6 py-3 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
					>
						Solicitar demo gratis
					</a>
				</div>
			</main>

			<BlogFooter />
		</div>
	);
}
